import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { DesktopCollectionCardSkeleton } from "../cards/DesktopCollectionCard";
import { SectionLink } from "../ui/SectionLink";
import { SectionTitle } from "../ui/SectionTitle";
import { ScrollArrowButton } from "../ui/ScrollArrowButton";

/** One card of the band, and the step the arrows scroll by (w-74 + gap-3). */
const CARD_STEP_PX = 308;

/** How wide the fog is at an edge that hides more cards (Figma "CollectionsBand"). */
const FOG_WIDTH = "1.5rem";

type HiddenEdges = { start: boolean; end: boolean };

/**
 * The row's fog, as a mask: an edge fades out only while cards hide past it,
 * so the first card sits sharp against the column before the reader scrolls
 * and the last one once the row reaches its end. A mask rather than a
 * gradient overlay, so it blends into whatever the band sits on.
 */
function fogMask(edges: HiddenEdges): string {
  const start = edges.start ? `transparent 0, black ${FOG_WIDTH}` : "black 0";
  const end = edges.end
    ? `black calc(100% - ${FOG_WIDTH}), transparent 100%`
    : "black 100%";
  return `linear-gradient(to right, ${start}, ${end})`;
}

/**
 * A horizontal band of collections in a desktop feed (Figma "CollectionsBand"):
 * the section title, "See all" and two arrows over a row of collection cards
 * that also scrolls with a trackpad. The caller draws each card, as a
 * `DesktopCollectionCard`.
 */
function DesktopCollectionsBand<TPlaylist extends { id: string }>({
  title,
  seeAllTo,
  playlists,
  isLoading,
  renderCard,
}: {
  title: string;
  seeAllTo: string;
  playlists: ReadonlyArray<TPlaylist>;
  isLoading: boolean;
  renderCard: (playlist: TPlaylist) => React.ReactNode;
}): React.JSX.Element | null {
  const { t } = useTranslation();
  // The row the arrows scroll, and which of its edges hide cards: UI
  // machinery, not page state.
  const rowRef = useRef<HTMLDivElement>(null);
  const [hiddenEdges, setHiddenEdges] = useState<HiddenEdges>({
    start: false,
    end: false,
  });

  useEffect(() => {
    const row = rowRef.current;
    if (row === null) return;
    function update(): void {
      if (row === null) return;
      const maxScroll = row.scrollWidth - row.clientWidth;
      setHiddenEdges({
        start: row.scrollLeft > 1,
        end: row.scrollLeft < maxScroll - 1,
      });
    }
    update();
    row.addEventListener("scroll", update, { passive: true });
    const observer = new ResizeObserver(update);
    observer.observe(row);
    return () => {
      row.removeEventListener("scroll", update);
      observer.disconnect();
    };
  }, [playlists.length, isLoading]);

  if (!isLoading && playlists.length === 0) return null;

  function scrollByCard(direction: -1 | 1) {
    const row = rowRef.current;
    if (row === null)
      throw new Error("should not happen: the band row is unmounted");
    row.scrollBy({ left: direction * CARD_STEP_PX, behavior: "smooth" });
  }

  return (
    <section className="flex flex-col gap-4 pt-5 pb-6">
      <div className="flex items-center justify-between gap-3">
        <SectionTitle title={title} />
        <div className="flex items-center gap-2">
          {playlists.length >= 2 ? (
            <SectionLink to={seeAllTo}>{t("See all")}</SectionLink>
          ) : null}
          {hiddenEdges.start || hiddenEdges.end ? (
            <>
              <ScrollArrowButton
                direction="previous"
                onClick={() => {
                  scrollByCard(-1);
                }}
              />
              <ScrollArrowButton
                direction="next"
                onClick={() => {
                  scrollByCard(1);
                }}
              />
            </>
          ) : null}
        </div>
      </div>
      {/* The padding, given back by the margin, leaves room for a card's focus
          ring, which stands 6px off the card: a scroller clips both axes. The
          bottom keeps more, for the desktop scrollbar under the cards: flush
          with their counts, it read as an underline. */}
      <div
        ref={rowRef}
        className="scrollbar-hidden -m-1.5 flex snap-x snap-mandatory scroll-px-1.5 gap-3 overflow-x-auto p-1.5 pb-4"
        style={{
          maskImage: fogMask(hiddenEdges),
          WebkitMaskImage: fogMask(hiddenEdges),
        }}
      >
        {isLoading
          ? [0, 1, 2].map((index) => (
              <div key={index} className="w-74 shrink-0 snap-start">
                <DesktopCollectionCardSkeleton />
              </div>
            ))
          : playlists.map((playlist) => (
              <div key={playlist.id} className="w-74 shrink-0 snap-start">
                {renderCard(playlist)}
              </div>
            ))}
      </div>
    </section>
  );
}

export { DesktopCollectionsBand };
